import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Match2BoosterType,
  Match2PowerupType,
} from 'src/replicant/match2/match2.ruleset';

type RandomBoxGame = 'randombox_slot_machine';

export type PinchResource = 'gems' | 'bPoints' | 'energy' | 'moves';
export type PinchCareActions = 'food' | 'snack' | 'toy' | 'first_aid';
export type PinchTrigger =
  | Match2BoosterType
  | Match2PowerupType
  | `careAction_${PinchCareActions}`
  | 'duo'
  | 'growthcapsule'
  | 'shop_energy'
  | 'shop_bPoints'
  | 'decoration'
  | 'daycare'
  | 'gacha'
  | 'gacha_reroll'
  | 'decor_progression'
  | RandomBoxGame
  | 'match2_shop_energy'
  | 'match2_shop_moves'
  | 'pet_progression'
  | 'redo_evolution'
  | 'match2';

export type PinchEventTrigger = PinchTrigger;

type PinchEventInternal = {
  trigger: PinchEventTrigger;
  allTimePinchCount: number;
  allTimePinchCountForResource: number;
  sessionCount: number;
  sessionCountForResource: number;
  prevoiusTrigger: PinchEventTrigger | null;
  conversionCount: number;
};

type PinchEvent = {
  resource: PinchResource;
  trigger: PinchEventTrigger;
  allTimePinchCount: number;
  allTimePinchCountForResource: number;
};

type PinchState = {
  events: Record<PinchResource, PinchEventInternal | null>;
  countsInSession: Record<PinchResource, number>;
  breadcrumbs: PinchEventTrigger[];
  conversionCrumbs: PinchResource[];
  lastPinchTrigger: PinchEventTrigger | null;
};

function breadcrumbsEqual(a: PinchResource[], b: PinchResource[]) {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((item) => b.includes(item));
}

const initialState: PinchState = {
  events: {
    gems: null,
    bPoints: null,
    energy: null,
    moves: null,
  },
  countsInSession: {
    gems: 0,
    bPoints: 0,
    energy: 0,
    moves: 0,
  },
  breadcrumbs: [],
  conversionCrumbs: [],
  lastPinchTrigger: null,
};

const slice = createSlice({
  name: 'pinch',

  initialState,

  reducers: {
    setLatestPinch(state, { payload }: PayloadAction<PinchEvent>) {
      const {
        resource,
        trigger,
        allTimePinchCount,
        allTimePinchCountForResource,
      } = payload;

      state.events[resource] = {
        trigger,
        allTimePinchCount,
        sessionCount: Object.values(state.countsInSession).reduce(
          (acc, a) => acc + a,
          0,
        ),
        sessionCountForResource: state.countsInSession[resource],
        allTimePinchCountForResource,
        prevoiusTrigger: state.lastPinchTrigger,
        conversionCount: 0,
      };

      state.countsInSession[payload.resource]++;
      state.lastPinchTrigger = trigger;

      if (!state.breadcrumbs.includes(trigger)) {
        state.breadcrumbs.push(trigger);
      }
    },

    setPinchConversion(state, { payload }: PayloadAction<PinchResource>) {
      const pinchEvent = state.events[payload];

      if (!pinchEvent) return;

      const pinchedResources = Object.keys(state.events).filter(
        (resource) => !!state.events[resource as PinchResource],
      ) as PinchResource[];

      if (!state.conversionCrumbs.includes(payload)) {
        state.conversionCrumbs.push(payload);
      }

      if (breadcrumbsEqual(pinchedResources, state.conversionCrumbs)) {
        state.breadcrumbs = [];
        state.conversionCrumbs = [];
      }

      pinchEvent.conversionCount++;
    },
  },
});

export const pinchActions = slice.actions;
export default slice.reducer;
